import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Primitive } from 'type-fest';

import {
    Anchor,
    Body1,
    Flex,
    Heading1,
    PinInput,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useHotkeys } from '@myphx-engineering/phx-design-system/hooks';
import { PhoneNumberFormatter } from '@phx/utils';

import { AccountOperationError } from '../../api/account-operation-error';
import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { sendVerificationCodeApi } from '../../api/send-verification-code.api';
import { verifyCodeApi } from '../../api/verify-code.api';
import { InfoMap } from '../../components/alerts/information/information-map';
import { PageLayout } from '../../components/layout/PageLayout';
import { CONSTANTS } from '../../constants';
import { telemetryInstance } from '../../instrumentation';
import type { ChallengeQuestionsFormModel } from '../../models/form.models';
import { routes } from '../../routes';
import { formatDateString } from '../../utils/date-formatter';
import { getErrorMessage } from '../../utils/get-error-message';

export type VerifyAccountPageState = {
    phoneNumber: string;
    password: string;
    isSignupFlow: boolean;
    callBackUrl: string;
    kratosSessionId?: string; // Kratos session ID from login API
    kratosSessionToken?: string; // Kratos session token from login API
    flowId?: string; // Flow ID from OTP request
    userId?: string; // Required in sign up flow
    email?: string; // Required in sign up flow
    firstName?: string; // Required in sign up flow
    lastName?: string; // Required in sign up flow
    dateOfBirth?: string; // Required in sign up flow
    address?: ChallengeQuestionsFormModel['address'] | null;
    partnerAccountConfiguration?: Record<string, Primitive>;
    unformattedPhoneNumber?: string;
};

const VerifyAccount = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { setBackOverrideState } = useNavBar();

    const state = location?.state as VerifyAccountPageState;

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [showResendMessage, setShowResendMessage] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');

    const ResendMessage = showResendMessage ? InfoMap['CodeResentInfo'] : null;

    const isFormValid = code.length === 6;

    const sanitizedFormState = useMemo(() => {
        return state
            ? {
                  ...state,
                  dob: formatDateString(state.dateOfBirth ?? ''),
                  password: '',
                  confirmPassword: '',
              }
            : undefined;
    }, [state]);

    const onNavigateBack = useCallback(() => {
        if (state?.isSignupFlow) {
            navigate(
                { pathname: routes.signUp, search: window.location.search },
                {
                    state: sanitizedFormState,
                }
            );
        } else {
            navigate(-1);
        }
    }, [navigate, state?.isSignupFlow, sanitizedFormState]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const onVerifyCodeClick = async (verificationCode: string) => {
        telemetryInstance.logEvent({
            name: 'OTPVerifySubmitted',
            source: location.pathname,
        });
        setErrorMessage(undefined);
        setShowResendMessage(false);
        setLoading(true);

        if (state.isSignupFlow) {
            // SIGNUP FLOW: Use OLD verification API
            const verifyResult = await verifyCodeApi(
                state.phoneNumber,
                verificationCode
            );

            if (!verifyResult.success) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(
                        `Code verification failed: ${verifyResult.error}`
                    ),
                });

                // If user is locked out (429), redirect back to sign-up with form data preserved
                if (verifyResult.error === '429_MaxAttemptReached') {
                    navigate(
                        {
                            pathname: routes.signUp,
                            search: window.location.search,
                        },
                        {
                            state: {
                                ...sanitizedFormState,
                                lockoutError: true,
                            },
                        }
                    );
                    return;
                }

                // If invalid code, check remaining attempts
                if (verifyResult.error === '400_InvalidCode') {
                    const lockoutStatus = await checkAccountLockoutApi(
                        state.phoneNumber
                    );
                    setLoading(false);
                    setErrorMessage(
                        getErrorMessage(
                            t,
                            verifyResult.error,
                            lockoutStatus.remainingAttempts
                        )
                    );
                    setCode('');
                    return;
                }

                setLoading(false);
                setErrorMessage(getErrorMessage(t, verifyResult.error));
                setCode('');
                return;
            }

            telemetryInstance.logEvent({
                name: 'PhoneVerified',
                source: location.pathname,
            });
            // OLD FLOW: In signup, account needs to be created now
            // Since we're using the old flow without Kratos session tokens,
            // we need to call the account creation API here
            telemetryInstance.logEvent({
                name: 'SignupPhoneVerified_CreatingAccount',
                source: location.pathname,
            });

            // Import and call createAccountApi
            const { createAccountApi } =
                await import('../../api/create-account.api');
            const createAccountResponse = await createAccountApi(
                state.firstName ?? '',
                state.lastName ?? '',
                state.email ?? '',
                state.dateOfBirth ?? '',
                state.phoneNumber,
                state.password ?? '',
                state.userId ?? '',
                state.address ?? null,
                state.partnerAccountConfiguration,
                undefined // brandId
            );

            if (!createAccountResponse.success) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new AccountOperationError(
                        createAccountResponse.error,
                        createAccountResponse.errorMessage,
                        'Account creation failed'
                    ),
                });
                setLoading(false);
                setErrorMessage(
                    getErrorMessage(t, createAccountResponse.error)
                );
                setCode('');
                return;
            }

            // Account created successfully, redirect to callback
            telemetryInstance.logEvent({
                name: 'SignupComplete_Redirect',
                source: location.pathname,
            });

            if (createAccountResponse.sessionId) {
                window.location.replace(
                    `${state.callBackUrl}&session=${createAccountResponse.sessionId}&authMethod=${CONSTANTS.defaultAuth}`
                );
            } else {
                // No session ID, show error
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(
                        'No session ID returned from account creation'
                    ),
                });
                setLoading(false);
                setErrorMessage(getErrorMessage(t, '500_GenericError'));
            }
        } else {
            const verifyResult = await verifyCodeApi(
                state.phoneNumber,
                verificationCode
            );

            if (!verifyResult.success) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(
                        `Code verification failed: ${verifyResult.error}`
                    ),
                });

                // If user is locked out (429), redirect back to sign-in with lockout flag and phone number
                if (verifyResult.error === '429_MaxAttemptReached') {
                    navigate(
                        {
                            pathname: routes.signIn,
                            search: window.location.search,
                        },
                        {
                            state: {
                                lockoutError: true,
                                phoneNumber: state.phoneNumber,
                            },
                        }
                    );
                    return;
                }

                // If invalid code, check remaining attempts
                if (verifyResult.error === '400_InvalidCode') {
                    const lockoutStatus = await checkAccountLockoutApi(
                        state.phoneNumber
                    );
                    setLoading(false);
                    setErrorMessage(
                        getErrorMessage(
                            t,
                            verifyResult.error,
                            lockoutStatus.remainingAttempts
                        )
                    );
                    setCode('');
                    return;
                }

                setLoading(false);
                setErrorMessage(getErrorMessage(t, verifyResult.error));
                setCode('');
                return;
            }

            telemetryInstance.logEvent({
                name: 'PhoneVerified',
                source: location.pathname,
            });

            // Redirect to callback - session should be handled by backend
            window.location.replace(
                `${state.callBackUrl}&session=${state.kratosSessionId}&authMethod=${CONSTANTS.defaultAuth}`
            );
        }
    };

    const onSendCodeClick = async () => {
        setLoading(true);

        const result = await sendVerificationCodeApi(state.phoneNumber);

        if (result.success) {
            setShowResendMessage(true);
            telemetryInstance.logEvent({
                name: 'OTPResent',
                source: location.pathname,
            });
            setLoading(false);
        } else {
            // If user is locked out (429), redirect back to sign-up or sign-in with lockout flag
            if (result.error === '429_MaxAttemptReached') {
                if (state.isSignupFlow) {
                    navigate(
                        {
                            pathname: routes.signUp,
                            search: window.location.search,
                        },
                        {
                            state: {
                                ...sanitizedFormState,
                                lockoutError: true,
                            },
                        }
                    );
                } else {
                    navigate(
                        {
                            pathname: routes.signIn,
                            search: window.location.search,
                        },
                        {
                            state: {
                                lockoutError: true,
                                phoneNumber: state.phoneNumber,
                            },
                        }
                    );
                }
                return;
            }

            setErrorMessage(getErrorMessage(t, result.error));
            setShowResendMessage(false);
            setLoading(false);
            telemetryInstance.logError({
                source: location.pathname,
                exception: new Error(result.error || 'OTP resend failed'),
            });
        }
    };

    useHotkeys([
        [
            'Enter',
            () => {
                if (isFormValid) {
                    onVerifyCodeClick(code);
                }
            },
        ],
    ]);

    return (
        <PageLayout
            loadingOverlay={loading}
            header={
                <>{showResendMessage && ResendMessage && <ResendMessage />}</>
            }
            heading={
                <Stack gap="sm">
                    <Heading1>
                        {t('verifyAccountPage.oneTimeVerificationCode')}
                    </Heading1>
                    {state?.phoneNumber ? (
                        <Body1>
                            {t('verifyAccountPage.sixDigitVerificationCode', {
                                phoneNumber: PhoneNumberFormatter.formatForUI(
                                    state.phoneNumber
                                ),
                            })}
                        </Body1>
                    ) : null}
                </Stack>
            }
        >
            <Stack gap="md">
                <PinInput
                    length={6}
                    size="xl"
                    gap="md"
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- tracked in docs/oxlint-violations.md
                    autoFocus
                    oneTimeCode
                    placeholder=""
                    value={code}
                    onChange={setCode}
                    onComplete={onVerifyCodeClick}
                    error={errorMessage}
                />
            </Stack>
            <Flex justify="left">
                <Body1>
                    {t('verifyAccountPage.receiveCode')}
                    <Anchor onClick={onSendCodeClick}>
                        {t('verifyAccountPage.resend')}
                    </Anchor>
                </Body1>
            </Flex>
        </PageLayout>
    );
};
export default VerifyAccount;
